import type {
  AuditLogActorOption,
  AuditLogEntry,
  AuditLogQuery,
} from '../types/audit-log.types';

export const ACTION_LABELS: Record<string, string> = {
  'campaigns.send': 'Campaign sent',
  'campaigns.schedule': 'Campaign scheduled',
  'campaigns.unschedule': 'Campaign unscheduled',
  'campaigns.testsend': 'Test email sent',
  'campaigns.delete': 'Campaign deleted',
  'sendjobs.pause': 'Sending paused',
  'sendjobs.resume': 'Sending resumed',
  'sendjobs.cancel': 'Sending cancelled',
  'suppression.create': 'Address suppressed',
  'suppression.delete': 'Suppression removed',
  'contacts.create': 'Contact created',
  'contacts.merge': 'Contact updated',
  'contacts.subscription': 'Subscription changed',
  'contacts.optin.confirm': 'Opt-in confirmed',
  'contacts.import': 'Contacts imported',
  'report.export.requested': 'Report export requested',
  'report.export.downloaded': 'Report export downloaded',
  'settings.update': 'Settings changed',
  'settings.systememail': 'System email edited',
  'settings.systememail.reset': 'System email reset',
};

export const getActionLabel = (action: string): string => {
  return ACTION_LABELS[action] ?? action;
};

export const getResourceLink = (entry: AuditLogEntry): string | null => {
  switch (entry.resourceType) {
    case 'campaign':
      return `/campaigns/${encodeURIComponent(entry.resourceId)}`;
    case 'sendjob':
      if (entry.metadata?.campaignId) {
        return `/campaigns/${encodeURIComponent(entry.metadata.campaignId)}`;
      }
      return null;
    case 'contact':
      return `/contacts/${encodeURIComponent(entry.resourceId)}`;
    case 'suppression':
      return '/suppression';
    case 'contactimport':
      return '/contacts/import';
    case 'report-export':
      return '/reports/overview';
    case 'settings':
      if (entry.metadata?.section === 'footer') return '/settings/legal-footer';
      if (entry.metadata?.section === 'branding') return '/settings/branding';
      if (entry.metadata?.section === 'optin') return '/settings/opt-in';
      if (entry.metadata?.section === 'sending') return '/sends';
      if (entry.metadata?.key || entry.action?.startsWith('settings.systememail'))
        return '/settings/system-emails';
      return '/settings';
    default:
      return null;
  }
};

export const actorKey = (a: AuditLogActorOption): string => `${a.actorType}:${a.actorId ?? ''}`;

export const actorToQuery = (
  a?: AuditLogActorOption
): Pick<AuditLogQuery, 'actorType' | 'actorId'> => {
  if (!a) return {};
  return a.actorId ? { actorType: a.actorType, actorId: a.actorId } : { actorType: a.actorType };
};

export const toRange = (
  fromDay?: string,
  toDay?: string
): { from?: string; to?: string } => {
  if (!fromDay && !toDay) return {};
  const from = fromDay ? new Date(`${fromDay}T00:00:00`).toISOString() : undefined;
  const to = toDay ? new Date(`${toDay}T23:59:59.999`).toISOString() : undefined;
  return { from, to };
};

export const formatLocalDateTime = (iso: string): string => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return `${d.toLocaleDateString('en-US', {
    month: 'short',
    day: '2-digit',
    year: 'numeric',
  })} · ${d.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
  })}`;
};

export const formatFullUtc = (iso: string): string => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return `${d.toISOString()} (UTC)`;
};

export const isForbiddenError = (error: unknown): boolean => {
  if (!error) return false;
  const status = (error as any)?.status ?? (error as any)?.statusCode;
  return status === 403;
};

export const getAuditErrorMessage = (error: unknown): string => {
  if (!error) return 'An unexpected error occurred';
  const err = error as any;
  if (Array.isArray(err?.errors) && err.errors.length > 0) {
    return err.errors.join('; ');
  }
  if (Array.isArray(err?.error?.errors) && err.error.errors.length > 0) {
    return err.error.errors.join('; ');
  }
  if (typeof err?.message === 'string') {
    return err.message;
  }
  return 'An unexpected error occurred';
};

export const cleanAuditQuery = (q: AuditLogQuery): Record<string, string | number> => {
  const result: Record<string, string | number> = {};
  for (const [key, value] of Object.entries(q)) {
    if (value !== undefined && value !== null && value !== '') {
      result[key] = value;
    }
  }
  return result;
};
